/*
 * Copyright 2022-2023. NXGN Management, LLC. All Rights Reserved.
 */

import { Inject, Injectable } from "@angular/core";
import { User } from "../models/user";
import { LOCAL_STORAGE, StorageService } from "ngx-webstorage-service";
import { REST_CONSTANTS, SECURITY_CONSTANTS } from "../utils/data.rest.constants";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { Buffer } from "buffer";

const USER_KEY = "user";
@Injectable({
  providedIn: "root"
})
export class AuthService {
  constructor(@Inject(LOCAL_STORAGE) private localStorage: StorageService, private http: HttpClient) {}

  user: User = new User();

  isUserLogged(): boolean {
    if (!this.user.logged) {
      this.checkUser();
    }
    return this.user && this.user.logged;
  }

  login(username: string, password: string): Observable<User> {
    const token = this.createToken(username, password);

    // try token and store if token is valid
    this.initializeUserObject(username, true, token);

    const url = `${REST_CONSTANTS.MERCHANT_NEW_BASE_URL}/user/roles`;
    return this.http.get<string[]>(url, { headers: REST_CONSTANTS.JSON_HEADER }).pipe(
      map((roles: string[]) => {
        this.user.roles = this.parseUserRoles(roles);
        this.localStorage.set(USER_KEY, this.user);
        return this.user;
      }),
      catchError(AuthService.handleError)
    );
  }

  createToken(username: string, password: string) {
    return "Basic " + Buffer.from(username + ":" + password).toString("base64");
  }

  getUserName(): string {
    return this.user.name;
  }

  getUserRole(roleCode: string): boolean {
    return this.user.roles.includes(roleCode);
  }

  public logout(): void {
    this.localStorage.remove(USER_KEY);

    // TODO We might need to do this once we get the merchant
    // MerchantService.clearMerchant();
    this.user = new User();
  }

  canUserAccess(what: string): boolean {
    return this.user.roles.includes(what);
  }

  /**
   * Initialization helper method
   * - reads stored user object from localStorage and initializes internal user object if possible
   */
  checkUser(): boolean {
    const storedUser = this.localStorage.get(USER_KEY);
    if (storedUser && storedUser.logged) {
      // TODO: try stored token
      this.user = storedUser;

      // check privileges
      if (!this.user.roles || 0 === this.user.roles.length) {
        this.user.roles = ["PROVISION"];
      }
      return true;
    }
    return false;
  }

  hasProvisionRole(): boolean {
    return this.canUserAccess("PROVISION");
  }

  hasImplementationRole(): boolean {
    return this.canUserAccess("IMPLEMENTATION");
  }

  isUserBase(): boolean {
    const conditions = ["PROVISION", "FINANCE", "IMPLEMENTATION"];
    return conditions.some((el) => this.user.roles.includes(el));
  }

  private parseUserRoles(rolesList: string[]): string[] {
    const roles: string[] = [];

    rolesList.forEach((role: string) => {
      const roleCode = this.getRoleCode(role);
      if (roleCode) {
        roles.push(roleCode);
      }
    });
    return roles;
  }

  private getRoleCode(restRole: string) {
    const userRole = SECURITY_CONSTANTS.LOGGED_USER_ROLES.find(({ value }) => value === restRole);

    return userRole ? userRole.code : "";
  }

  // Private/Internal
  private initializeUserObject(username: string, logged: boolean, token: string) {
    this.user.logged = logged;
    this.user.name = username;
    this.user.token = token;
    this.user.roles = [];

    this.localStorage.set(USER_KEY, this.user);
  }

  private static handleError(error: HttpErrorResponse) {
    // Return an observable with a user-facing error message.
    return throwError(error);
  }
}
